import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import type { Workspace } from '@/types/entities'

/**
 * Resolves the `:workspaceSlug` in the URL to a real workspace record.
 *
 * This exists because the two sides disagree about identity: URLs carry a human-readable
 * slug ("product") so links are legible, but every API takes a workspace ID
 * ("wsp_product"). Passing the slug straight through returns an empty list rather than an
 * error, which looks like "the app is broken" instead of "wrong identifier".
 *
 * Feature code must use `useWorkspace().workspaceId` — never the raw route param.
 */
interface WorkspaceContextValue {
  workspace: Workspace | null
  workspaceId: string
  workspaces: Workspace[]
  isPending: boolean
}

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspace: null,
  workspaceId: '',
  workspaces: [],
  isPending: true,
})

export function WorkspaceProvider({
  children,
  fallback,
  onMissing,
}: {
  children: ReactNode
  fallback?: ReactNode
  onMissing?: (slug: string, available: Workspace[]) => ReactNode
}) {
  const { workspaceSlug = '' } = useParams()

  const query = useQuery({
    queryKey: qk.workspaces(),
    queryFn: () => apiGet<{ items: Workspace[] }>('workspaces'),
    staleTime: 5 * 60_000,
  })

  const workspaces = useMemo(() => query.data?.items ?? [], [query.data])

  const workspace = useMemo(
    () => workspaces.find((w) => w.slug === workspaceSlug || w.id === workspaceSlug) ?? null,
    [workspaces, workspaceSlug],
  )

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspace,
      workspaceId: workspace?.id ?? '',
      workspaces,
      isPending: query.isPending,
    }),
    [workspace, workspaces, query.isPending],
  )

  if (query.isPending) return <>{fallback ?? null}</>
  if (!workspace && onMissing) return <>{onMissing(workspaceSlug, workspaces)}</>

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext)
}
